/**
 * useHostLensPolling — the Host lens's cached-polling hook (Story H).
 *
 * The cross-run sibling of {@link useBoardPolling}: it polls `GET /host-lens`
 * (every run's trials interleaved) plus `GET /status` (the host overlay the lens
 * leads with) on the same relaxed interval, threads `If-None-Match`/`304` so an
 * idle lens transfers almost nothing, and retains the last good snapshot across a
 * transient failure so the lens ages honestly (`updated Ns ago` → `disconnected`)
 * instead of blanking. The lens has no run id — it is the whole host — so the
 * effect re-arms only when the injected impls/interval change.
 */

import { useEffect, useRef, useState } from 'react'
import { type HostLensResult, fetchHostLens, fetchHostStatus } from './board-client'
import type { HostStatus } from './board-host'
import type { HostLensSnapshot } from './board-types'
import { ReadApiError, sessionFailureOf } from './client'
import type { SessionFailureKind } from './session'

/** The default relaxed poll interval (ms) — within the spec's 2–5s band. */
export const DEFAULT_LENS_POLL_MS = 3_000

/** Injection points so tests drive the hook deterministically (no real timers/fetch). */
export interface HostLensPollingDeps {
  fetchLensImpl?: typeof fetchHostLens
  fetchHostImpl?: typeof fetchHostStatus
  intervalMs?: number
}

/** The lens view state the hook exposes to the React layer. */
export interface HostLensPollingState {
  /** The latest lens snapshot, or null before the first successful poll (loading). */
  snapshot: HostLensSnapshot | null
  /** The latest host status the overlay leads with, or null until the first `/status`. */
  host: HostStatus | null
  /** A read error to surface honestly, or null. Retained snapshot stays visible. */
  error: { endpoint: string; message: string } | null
  /**
   * Set when the last failure was an identity failure rather than a transport
   * one. Kept separate from `error` because the two demand opposite treatments:
   * a transport blip should retain the snapshot and age it, while an identity
   * failure must replace the board with a session state — an aged snapshot behind
   * a dead session is the empty-but-healthy lie.
   */
  sessionFailure: SessionFailureKind | null
  /** True once a poll has failed mid-session while a prior snapshot is retained. */
  stale: boolean
  /** Epoch ms of the last successful contact (200 or 304), or null before the first. */
  lastSuccessAt: number | null
}

/**
 * Poll the Host lens on a relaxed interval. Returns the lens view state; the
 * container renders {@link HostLens} from it. Retains the last good snapshot
 * across a transient failure so the lens ages honestly instead of blanking
 * (Principle 3 — never an endless spinner once data has arrived).
 */
export function useHostLensPolling(deps: HostLensPollingDeps = {}): HostLensPollingState {
  const intervalMs = deps.intervalMs ?? DEFAULT_LENS_POLL_MS
  const fetchLensImpl = deps.fetchLensImpl ?? fetchHostLens
  const fetchHostImpl = deps.fetchHostImpl ?? fetchHostStatus

  const [state, setState] = useState<HostLensPollingState>({
    snapshot: null,
    host: null,
    error: null,
    sessionFailure: null,
    stale: false,
    lastSuccessAt: null,
  })
  // The held ETag rides outside React state so a re-render never re-arms a fetch.
  const etagRef = useRef<string | null>(null)
  // Monotonic poll sequence: when latency exceeds the interval two polls overlap,
  // and the slower one must not clobber the newer one's ETag or snapshot.
  const pollSeqRef = useRef(0)

  useEffect(() => {
    let live = true
    etagRef.current = null
    pollSeqRef.current = 0
    setState({ snapshot: null, host: null, error: null, sessionFailure: null, stale: false, lastSuccessAt: null })

    const poll = async (): Promise<void> => {
      const seq = ++pollSeqRef.current
      try {
        // Fetch up to the server's MAX_PAGE_LIMIT (1 000) so done trials past the
        // default 200-item page are included — active trials sort first on the server,
        // so without an explicit limit a long run's Done column can appear nearly
        // empty even though the PipelineBar shows hundreds of finished trials.
        const [lens, host] = await Promise.all([
          fetchLensImpl({ etag: etagRef.current, limit: 1_000 }),
          fetchHostImpl(),
        ])
        if (!live || seq !== pollSeqRef.current) {
          return
        }
        applyResult(lens, host)
      } catch (err) {
        if (!live || seq !== pollSeqRef.current) {
          return
        }
        applyError(err)
      }
    }

    const applyResult = (lens: HostLensResult, host: HostStatus | null): void => {
      if (lens.etag !== null) {
        etagRef.current = lens.etag
      }
      setState((prev) => ({
        // On a 304 the snapshot is null; keep the last good one.
        snapshot: lens.snapshot ?? prev.snapshot,
        host: host ?? prev.host,
        error: null,
        sessionFailure: null,
        stale: false,
        // A 304 is still a successful contact — the data is current as of now.
        lastSuccessAt: Date.now(),
      }))
    }

    const applyError = (err: unknown): void => {
      const endpoint = err instanceof ReadApiError ? err.endpoint : '/host-lens'
      const message = err instanceof Error ? err.message : 'read failed'
      setState((prev) => ({
        snapshot: prev.snapshot,
        host: prev.host,
        error: { endpoint, message },
        sessionFailure: sessionFailureOf(err),
        stale: prev.snapshot !== null,
        lastSuccessAt: prev.lastSuccessAt,
      }))
    }

    void poll()
    const timer = setInterval(() => void poll(), intervalMs)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [fetchLensImpl, fetchHostImpl, intervalMs])

  return state
}
